import { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ArticleReactionType, ArticleReactionResponse } from '@/lib/api';
import { trackReactionClick, trackReactionSessionStreak } from '@/lib/analytics';

const REACTIONS: { type: ArticleReactionType; emoji: string; label: string }[] = [
  { type: 'hot', emoji: '\u{1F525}', label: 'Hot' },
  { type: 'agree', emoji: '\u{1F44D}', label: 'Agree' },
  { type: 'surprised', emoji: '\u{1F62E}', label: 'Surprised' },
  { type: 'thinking', emoji: '\u{1F914}', label: 'Thinking' },
  { type: 'disagree', emoji: '\u{1F44E}', label: 'Disagree' },
];

function getVisitorId(): string {
  const key = 'mw_visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

interface FeedArticleReactionsProps {
  postId: string;
}

export function FeedArticleReactions({ postId }: FeedArticleReactionsProps) {
  const queryClient = useQueryClient();
  const visitorId = useRef(getVisitorId());
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Lazy-load: only fetch when scrolled into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data } = useQuery({
    queryKey: ['articleReactions', postId],
    queryFn: () => api.reactions.getArticle(postId, visitorId.current),
    staleTime: 60_000,
    enabled: isVisible,
  });

  const counts = data?.counts ?? { hot: 0, agree: 0, surprised: 0, thinking: 0, disagree: 0 };
  const selected = data?.userReaction ?? null;
  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  const mutation = useMutation({
    mutationFn: (type: ArticleReactionType) =>
      api.reactions.reactArticle(postId, type, visitorId.current),
    onMutate: async (type) => {
      await queryClient.cancelQueries({ queryKey: ['articleReactions', postId] });
      const previous = queryClient.getQueryData<ArticleReactionResponse>(['articleReactions', postId]);

      queryClient.setQueryData<ArticleReactionResponse>(['articleReactions', postId], (old) => {
        if (!old) return old;
        const next = { ...old.counts };
        if (old.userReaction === type) {
          next[type] = Math.max(0, next[type] - 1);
          return { counts: next, userReaction: null };
        }
        if (old.userReaction) next[old.userReaction] = Math.max(0, next[old.userReaction] - 1);
        next[type] = next[type] + 1;
        return { counts: next, userReaction: type };
      });

      return { previous };
    },
    onError: (_err, _type, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['articleReactions', postId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['articleReactions', postId] });
    },
  });

  const handleReaction = (type: ArticleReactionType, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    mutation.mutate(type);
    trackReactionClick(postId, type, totalCount);
    trackReactionSessionStreak(postId, type);
  };

  // Don't render buttons until data loads; show placeholder
  if (!isVisible || !data) {
    return (
      <div ref={containerRef} className="flex items-center gap-0.5" aria-label="Article reactions">
        {REACTIONS.map(({ type, emoji, label }) => (
          <span
            key={type}
            title={label}
            className="flex items-center gap-0.5 rounded-md px-1.5 py-1 text-xs text-text-quaternary"
          >
            <span className="text-sm opacity-50">{emoji}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex items-center gap-0.5" role="group" aria-label="Article reactions">
      {REACTIONS.map(({ type, emoji, label }) => {
        const isActive = selected === type;
        const count = counts[type];
        return (
          <button
            key={type}
            type="button"
            onClick={(e) => handleReaction(type, e)}
            disabled={mutation.isPending}
            aria-label={isActive ? `Remove ${label} reaction` : `${label} reaction`}
            aria-pressed={isActive}
            title={label}
            className={`relative z-10 flex items-center gap-0.5 rounded-md px-1.5 py-1 text-xs font-medium transition-all duration-200 active:scale-95 disabled:opacity-50 ${
              isActive
                ? 'bg-primary-500/15 text-primary-400 ring-1 ring-primary-500/30'
                : 'text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary'
            }`}
          >
            <span className={`text-sm ${isActive ? 'scale-110' : ''}`}>{emoji}</span>
            {count > 0 && <span className="tabular-nums text-[10px] leading-none">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
